/**
 * Every source the calculator's numbers come from, shown on the page (Sources & methodology). Kept
 * next to the copy that cites them; the code files carry the same references in their headers.
 * Hand-branched per locale like seo-copy.ts: the institution and document names are proper nouns,
 * only the "used for" note is translated.
 */
import type { CopyLocale } from './seo-copy';

export interface SourceLink {
  org: string;
  title: string;
  url: string;
  usedFor: Record<CopyLocale, string>;
}

export const SOURCES: SourceLink[] = [
  {
    org: 'Verohallinto',
    title: 'Valtion tuloveroasteikko ja vähennykset 2026',
    url: 'https://www.vero.fi/henkiloasiakkaat/verokortti-ja-veroilmoitus/tulot/ansiotulot/',
    usedFor: { en: 'State income tax scale, earned-income credit, work-expense and commuting deductions, Yle tax', fi: 'Valtion tuloveroasteikko, työtulovähennys, tulonhankkimis- ja matkakuluvähennys, yle-vero', sv: 'Statens inkomstskatteskala, arbetsinkomstavdrag, avdrag för utgifter och resor, Yle-skatt' },
  },
  {
    org: 'Verohallinto',
    title: 'Kuntien ja seurakuntien tuloveroprosentit vuonna 2026',
    url: 'https://www.vero.fi/syventavat-vero-ohjeet/paatokset/47465/kuntien-ja-seurakuntien-tuloveroprosentit-vuonna-2026/',
    usedFor: { en: 'Municipal and parish tax rates for all 308 municipalities', fi: 'Kaikkien 308 kunnan kunnallis- ja kirkollisveroprosentit', sv: 'Kommunal- och kyrkoskattesatser för alla 308 kommuner' },
  },
  {
    org: 'Kuntaliitto',
    title: 'Kuntien veroprosentit 2026',
    url: 'https://www.kuntaliitto.fi/talous-ja-elinvoima/verotus/kuntien-veroprosentit',
    usedFor: { en: 'Cross-check of municipal rates and the income-weighted national average (7.57%)', fi: 'Kunnallisveroprosenttien ristiintarkistus ja tulopainotettu keskiarvo (7,57 %)', sv: 'Kontroll av kommunalskattesatserna och det inkomstvägda medeltalet (7,57 %)' },
  },
  {
    org: 'Eläketurvakeskus',
    title: 'Työeläkemaksut vuonna 2026',
    url: 'https://www.etk.fi/ajankohtaista/tyoelakemaksut-vuonna-2026/',
    usedFor: { en: 'Employee and employer TyEL pension contributions by age', fi: 'Työntekijän ja työnantajan TyEL-maksut ikäryhmittäin', sv: 'Arbetstagarens och arbetsgivarens ArPL-avgifter enligt ålder' },
  },
  {
    org: 'Työllisyysrahasto',
    title: 'Vuoden 2026 työttömyysvakuutusmaksut',
    url: 'https://www.tyollisyysrahasto.fi/uutiset/vuoden-2026-tyottomyysvakuutusmaksut-on-vahvistettu/',
    usedFor: { en: 'Employee and employer unemployment insurance contributions', fi: 'Työntekijän ja työnantajan työttömyysvakuutusmaksut', sv: 'Arbetstagarens och arbetsgivarens arbetslöshetsförsäkringsavgifter' },
  },
  {
    org: 'Verohallinto',
    title: 'Työnantajan sairausvakuutusmaksu 2026',
    url: 'https://www.vero.fi/syventavat-vero-ohjeet/ohje-hakusivu/338440/tyonantajan-sairausvakuutusmaksu2/',
    usedFor: { en: "Employer's health insurance contribution", fi: 'Työnantajan sairausvakuutusmaksu', sv: 'Arbetsgivarens sjukförsäkringsavgift' },
  },
  {
    org: 'Verohallinto',
    title: 'Arvonlisäverokannat',
    url: 'https://www.vero.fi/en/businesses-and-corporations/taxes-and-charges/vat/rates-of-vat/',
    usedFor: { en: 'VAT rates (25.5%, 14%, 10%) behind the VAT-on-spending estimate', fi: 'Arvonlisäverokannat (25,5 %, 14 %, 10 %) kulutuksen alv-arvion pohjana', sv: 'Momssatserna (25,5 %, 14 %, 10 %) bakom uppskattningen av moms på konsumtion' },
  },
  {
    org: 'Tilastokeskus',
    title: 'Kotitalouksien kulutusmenot tuloviidenneksittäin (StatFin)',
    url: 'https://pxdata.stat.fi/PXWeb/pxweb/fi/StatFin/StatFin__ktutk/',
    usedFor: { en: 'Spending share and consumption mix by income quintile for the VAT estimate', fi: 'Kulutusosuus ja kulutusrakenne tuloviidenneksittäin alv-arviota varten', sv: 'Konsumtionsandel och konsumtionsmix per inkomstkvintil för momsuppskattningen' },
  },
  {
    org: 'Verohallinto',
    title: 'Osinkojen ja luovutusvoittojen verotus',
    url: 'https://www.vero.fi/henkiloasiakkaat/omaisuus/sijoitukset/osingot/',
    usedFor: { en: 'Capital income rates, listed dividends, the unlisted-company dividend rule', fi: 'Pääomatuloveroprosentit, pörssiosingot, listaamattoman yhtiön osinkosääntö', sv: 'Kapitalinkomstskattesatser, börsdividender, regeln för dividender från onoterade bolag' },
  },
  {
    org: 'Veronmaksajain Keskusliitto',
    title: 'Palkansaajan veroprosentit 2026',
    url: 'https://www.veronmaksajat.fi/tutkimus-ja-tilastot/tuloverot/palkansaajan-veroprosentit/',
    usedFor: { en: 'Reference table the engine is tested against (to the euro at 15 income levels)', fi: 'Vertailutaulukko, jota vasten laskuri testataan (euron tarkkuudella 15 tulotasolla)', sv: 'Referenstabell som räknaren testas mot (på euron när vid 15 inkomstnivåer)' },
  },
  {
    org: 'Valtiovarainministeriö',
    title: 'Valtion talousarvio 2026',
    url: 'https://budjetti.vm.fi/sisalto.jsp?year=2026&lang=fi&maindoc=/2026/tae/hallituksenEsitys/hallituksenEsitys.xml',
    usedFor: { en: 'How the state spends its money (appropriations by main class and the pass-through items)', fi: 'Mihin valtio käyttää rahansa (määrärahat pääluokittain ja läpikulkuerät)', sv: 'Vad staten använder pengarna till (anslag per huvudtitel och genomströmningsposter)' },
  },
  {
    org: 'Kuntaliitto',
    title: 'Kuntien palvelutuotannon nettokäyttökustannukset 2025',
    url: 'https://www.kuntaliitto.fi/talous-ja-elinvoima/kuntatalouden-tilastot',
    usedFor: { en: 'How each municipality spends its money, by task (Valtiokonttori data)', fi: 'Mihin kukin kunta käyttää rahansa tehtävittäin (Valtiokonttorin aineisto)', sv: 'Vad varje kommun använder pengarna till, per uppgift (Statskontorets data)' },
  },
  {
    org: 'Tilastokeskus',
    title: 'Julkisyhteisöjen menot tehtävittäin 2024',
    url: 'https://www.veronmaksajat.fi/tutkimus-ja-tilastot/suomen-verot-ja-menot/julkiset-menot/',
    usedFor: { en: 'Whole-public-sector spending by function, used as a check on the split', fi: 'Koko julkisen sektorin menot tehtävittäin, jaon tarkistukseen', sv: 'Hela den offentliga sektorns utgifter per funktion, som kontroll av fördelningen' },
  },
];
